import { css, useTheme } from '@emotion/react';
import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

interface FolderDialogProps {
  isOpen: boolean;
  onSubmit: (name: string) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  initialName?: string;
  title: string;
}

export function FolderDialog({
  isOpen,
  onSubmit,
  onCancel,
  isSubmitting,
  initialName = '',
  title,
}: FolderDialogProps) {
  const theme = useTheme();
  const [name, setName] = useState(initialName);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      // Focus input after render
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [isOpen, initialName]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (trimmed) {
      onSubmit(trimmed);
    }
  };

  return (
    <div
      css={css`
        position: fixed;
        inset: 0;
        background: ${theme.colors.overlay};
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 50;
      `}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="folder-dialog-title"
        css={css`
          background: ${theme.colors.surface};
          border: 1px solid ${theme.colors.border};
          border-radius: ${radii.md};
          padding: ${spacing.xl};
          max-width: 400px;
          width: 90%;
          box-shadow: ${shadows.prominent};
        `}
      >
        <h2
          id="folder-dialog-title"
          css={css`
            margin: 0 0 ${spacing.sm};
            font-size: ${fontSizes.xl};
            color: ${theme.colors.text};
          `}
        >
          {title}
        </h2>
        <form onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            type="text"
            aria-label="Folder name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isSubmitting}
            css={css`
              width: 100%;
              box-sizing: border-box;
              padding: ${spacing.sm} ${spacing.md};
              font-size: ${fontSizes.md};
              color: ${theme.colors.text};
              background: ${theme.colors.background};
              border: 1px solid ${theme.colors.border};
              border-radius: ${radii.sm};
              margin-bottom: ${spacing.xl};

              &:focus {
                outline: none;
                border-color: ${theme.colors.primary};
              }

              &:disabled {
                opacity: 0.5;
              }
            `}
          />
          <div
            css={css`
              display: flex;
              gap: ${spacing.sm};
              justify-content: flex-end;
            `}
          >
            <Button
              variant="secondary"
              shape="rect"
              size="sm"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              shape="rect"
              size="sm"
              disabled={isSubmitting || !name.trim()}
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.xs};
              `}
            >
              {isSubmitting && <Spinner size="sm" aria-hidden />}
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
